import React, { useState, useEffect } from 'react';
import { SubscriptionContext, SubscriptionContextType, Subscription, SubscriptionPlan } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';
import { toast } from "sonner";
import { 
  verifySubscriptionStatus, 
  getPaymentBackup, 
  recoverSubscriptionFromPayment, 
  clearPaymentBackup,
  hasAttemptedRecovery,
  markRecoveryAttempted,
} from '@/utils/PaymentHelper';
import { checkFreePlanExpiry, showFreePlanNotification } from '@/utils/freePlanHelpers';
import { trackSubscriptionEvent } from '@/utils/subscriptionAnalytics';
import { 
  markPlanExpired, 
  isInPostExpiryGracePeriod 
} from '@/utils/SubscriptionRedirectHelper';

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription>({ 
    has_active_subscription: false, 
    subscription: null 
  });
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([
    {
      id: 'free',
      name: 'Free',
      description: '7-day access to basic features',
      price_monthly: 0,
      price_yearly: 0,
      features: [
        'Adaptive Quiz Assessment',
        'Advanced Analytics',
        'Adaptive Study Notes',
        'Adaptive Current Affairs',
        'Adaptive Flash Cards',
        '7-day access period'
      ],
      is_trial: false
    },
    {
      id: 'basic',
      name: 'Basic',
      description: 'Standard access with more features',
      price_monthly: 100,
      price_yearly: 900,
      features: [
        'Adaptive Quiz Assessment',
        'Advanced Analytics',
        'Adaptive Study Notes',
        'Adaptive Current Affairs',
        'Adaptive Flash Cards',
        'Full access to all flashcard sets',
        'Personalized study planner'
      ],
      is_trial: false
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Best for serious learners',
      price_monthly: 200,
      price_yearly: 1800,
      features: [
        'All Basic Plan Features',
        'Adaptive Learning Path',
        'Adaptive Video Recommendation',
        'Adaptive Mock Tests',
        'PYQ Multiplier',
        'AI Learning Studio',
        'Collaborative Learning',
        'Code Playground',
        'Advanced Analytics & Skill-gap Analysis',
        '24/7 AI Mentor Chatbot and Voice Tutor',
        'Advanced AI-driven personalization',
        'Exclusive forums & live Q&A sessions',
        'Advanced certificates',
        'Priority Support'
      ],
      is_trial: false
    },
    {
      id: 'trial',
      name: 'Trial',
      description: 'Full access to all features for 7 days',
      price_monthly: 20,
      price_yearly: 20,
      features: [
        'Adaptive Quiz Assessment',
        'Advanced Analytics',
        'Adaptive Study Notes',
        'Adaptive Current Affairs',
        'Adaptive Flash Cards',
        'Full access to all flashcard sets',
        'Adaptive Learning Path',
        'Adaptive Video Recommendation',
        'Adaptive Mock Tests',
        'PYQ Multiplier',
        'AI Learning Studio',
        'Collaborative Learning',
        'Code Playground',
        'Advanced Analytics & Skill-gap Analysis',
        '24/7 AI Mentor Chatbot and Voice Tutor',
        'Advanced AI-driven personalization',
        'Exclusive forums & live Q&A sessions',
        'Advanced certificates',
        'Priority Support'
      ],
      is_trial: true
    }
  ]);
  const { session } = useAuthSession();
  
  // Track if we're in a retry cycle to avoid infinite loops
  const [isRetrying, setIsRetrying] = useState(false);
  const maxRetries = 2;
  const [retryCount, setRetryCount] = useState(0);
  // Track last refresh time to prevent frequent refreshes
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const minRefreshInterval = 3000; // 3 seconds minimum between refreshes

  const fetchSubscriptionData = async (silent = false) => {
    if (!session?.user) {
      setSubscription({
        has_active_subscription: false,
        subscription: null
      });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Use the new unified subscription expiry checker
      const { data: subscriptionData, error } = await supabase.functions.invoke('check-subscription-expiry');
      
      if (error) {
        console.error('Error checking subscription expiry:', error);
        // Fall back to the old method
        const fallbackData = await verifySubscriptionStatus(hasAttemptedRecovery());
        handleSubscriptionResult(fallbackData, silent);
      } else {
        // Handle new expiry checker response format
        handleSubscriptionResult(subscriptionData, silent);
      }
      
      // Fetch subscription plans from database
      const { data: plansData, error: plansError } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price_monthly', { ascending: true });

      if (plansError) {
        console.error('Error fetching subscription plans:', plansError);
      } else if (plansData && plansData.length > 0) {
        // Use database plans and ensure proper structure
        const formattedPlans = plansData.map(plan => ({
          id: plan.id,
          name: plan.name,
          description: plan.description,
          price_monthly: plan.price_monthly,
          price_yearly: plan.price_yearly,
          features: plan.features || [],
          is_trial: plan.is_trial || false
        }));
        setSubscriptionPlans(formattedPlans);
        console.log('Updated subscription plans from database:', formattedPlans);
      }
      
      // Update last refresh time
      setLastRefreshTime(Date.now());
    } catch (error) {
      console.error('Error in subscription data fetch:', error);
      
      // Only show a toast if we're not in a retry cycle and not in silent mode
      if (!isRetrying && !silent) {
        toast.error("There was a problem loading your subscription data");
      }
      
      // Reset subscription state on error
      setSubscription({
        has_active_subscription: false,
        subscription: null
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to process subscription data result
  const handleSubscriptionResult = (data: any, silent: boolean) => {
    if (data) {
      setSubscription(data);
      
      // Track when a plan expires or is about to expire
      const planName = data.subscription?.plan;
      const planType = data.subscription?.is_trial ? 'trial' : 
                       (planName === 'Free' || planName === 'Starter') ? 'free' : 'paid';
      
      // If the plan is expired, mark it for grace period
      if (data.expired) {
        // Mark the plan as expired to start grace period
        markPlanExpired(planType);
        
        // Track plan expired event
        trackSubscriptionEvent('plan_expired', {
          plan_type: planType,
          metadata: { plan_name: planName }
        });
      }
      
      // Handle Free plan specific notifications
      if (data.has_active_subscription && 
          (planName === 'Free' || planName === 'Starter') && 
          data.days_remaining !== undefined) {
        
        showFreePlanNotification(data.days_remaining);
        
        // Track days remaining updates
        trackSubscriptionEvent('days_remaining_update', {
          plan_type: 'free',
          days_remaining: data.days_remaining,
          metadata: { plan_name: planName }
        });
      }
    } else {
      setSubscription({
        has_active_subscription: false,
        subscription: null
      });
    }
  };

  // Handle potential recovery on mount
  useEffect(() => {
    const attemptRecoveryIfNeeded = async () => {
      // Skip recovery if:
      // 1. User not logged in
      // 2. Recovery already attempted this session
      // 3. Already has active subscription
      if (!session?.user?.id || hasAttemptedRecovery() || subscription.has_active_subscription) {
        return;
      }
      
      const backup = getPaymentBackup();
      if (backup && (backup.paymentId || backup.orderId)) {
        console.log('Found payment backup during context init, attempting recovery:', backup);
        markRecoveryAttempted(); // Mark recovery as attempted to prevent duplicates
        
        try {
          // Don't await this so we don't block rendering
          // Pass false to prevent toast notifications here, let the component handle it
          recoverSubscriptionFromPayment(backup.paymentId, backup.orderId, false)
            .then(success => {
              if (success) {
                console.log('Successfully recovered subscription from payment during context init');
                fetchSubscriptionData(true); // Silent refresh
              } else {
                // On failed recovery, clear the backup to prevent repeated attempts
                console.log('Failed to recover subscription during context init');
                clearPaymentBackup();
              }
            })
            .catch(error => {
              console.error('Error recovering subscription during context init:', error);
              clearPaymentBackup();
            });
        } catch (err) {
          console.error('Exception during recovery attempt on context init:', err);
        }
      }
    };
    
    // Try recovery with a small delay to avoid race conditions
    const timer = setTimeout(() => {
      attemptRecoveryIfNeeded();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [session?.user?.id, subscription.has_active_subscription]);

  useEffect(() => {
    // Only fetch subscription data if we have a valid session
    if (session?.user?.id) {
      fetchSubscriptionData(true); // Silent on first load
      
      // Set up subscription to listen for changes
      const subscriptionChannel = supabase
        .channel('subscription-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_subscriptions',
            filter: `user_id=eq.${session.user.id}`
          },
          () => {
            fetchSubscriptionData(true); // Silent on DB changes
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(subscriptionChannel);
      };
    } else {
      // Reset subscription state when not authenticated
      setSubscription({
        has_active_subscription: false,
        subscription: null
      });
      setIsLoading(false);
    }
  }, [session?.user?.id]);

  const refreshSubscription = async () => {
    // Prevent rapid refreshes
    const now = Date.now();
    if (now - lastRefreshTime < minRefreshInterval) {
      console.log('Skipping refresh, too soon since last refresh');
      return;
    }
    
    if (!session?.user) {
      toast.error("You must be logged in to refresh subscription status");
      return;
    }
    
    try {
      await fetchSubscriptionData();
      
      // If subscription data fetch fails with no active subscription and we haven't
      // exceeded retry attempts, try subscription recovery
      if (!subscription.has_active_subscription && retryCount < maxRetries && !hasAttemptedRecovery()) {
        setIsRetrying(true);
        setRetryCount(prev => prev + 1);
        
        const backup = getPaymentBackup();
        if (backup && (backup.paymentId || backup.orderId)) {
          // Mark as attempted before actual attempt to prevent duplicates
          markRecoveryAttempted();
          
          const success = await recoverSubscriptionFromPayment(
            backup.paymentId, 
            backup.orderId, 
            false // No toast messages from here
          );
          
          if (success) {
            // Refetch subscription data after successful recovery
            toast.success("Successfully recovered your subscription!");
            await fetchSubscriptionData(true); // Silent refresh
          } else {
            clearPaymentBackup();
            // Only show error if we're on the last retry
            if (retryCount >= maxRetries - 1) {
              toast.error("Could not recover subscription. Please contact support.");
            }
          }
        }
        
        setIsRetrying(false);
      }
    } catch (error) {
      console.error('Error in refreshSubscription:', error);
      toast.error("Failed to refresh subscription status");
    }
  };

  const value: SubscriptionContextType = {
    subscription,
    isLoading,
    subscriptionPlans,
    refreshSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
